import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Vehicle, LimoAnywhereVehicleType } from '@limo/database'

import { Button } from '@/components/ui/button'
import { VehicleForm } from '@/components/vehicles/VehicleForm'
import { Loader2, ArrowLeft } from 'lucide-react'

export function VehicleFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()

  const isEditing = !!id
  const activeAccount = isImpersonating ? impersonatedAccount : account

  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  const { data: vehicle, isLoading: isLoadingVehicle } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as Vehicle
    },
    enabled: !!id,
  })

  // Account settings the vehicle form needs to warn about configuration that
  // silently does nothing: quote-only notifications that can't be delivered, and
  // either custom-quote setting while the account's master switch is off.
  const { data: quoteSettings } = useQuery({
    queryKey: ['vehicle-quote-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return null
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', activeAccount.id)
        .in('key', [
          'go_high_level_enabled',
          'operator_quote_notify_phone',
          'operator_quote_notify_email',
          'booking_custom_quotes_enabled',
        ])
      if (error) throw error
      const settings = Object.fromEntries(data.map((s) => [s.key, s.value]))
      return {
        notify: {
          ghlEnabled: settings.go_high_level_enabled === 'true',
          hasNotifyContact: Boolean(
            settings.operator_quote_notify_phone ||
              settings.operator_quote_notify_email
          ),
        },
        customQuotes: {
          enabled: settings.booking_custom_quotes_enabled === 'true',
        },
      }
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch Limo Anywhere vehicle types for the dropdown
  const { data: laVehicleTypes } = useQuery({
    queryKey: ['limo-anywhere-vehicle-types', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('limo_anywhere_vehicle_types')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('name')
      if (error) throw error
      return data as LimoAnywhereVehicleType[]
    },
    enabled: !!activeAccount?.id,
  })

  if (!activeAccount) {
    return null
  }

  if (isEditing && (isLoadingVehicle || !vehicle)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`${basePath}/vehicles`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Edit Vehicle' : 'Add Vehicle'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing
              ? 'Update the vehicle information'
              : 'Add a new vehicle to your fleet'}
          </p>
        </div>
      </div>

      {/*
        Keyed on the vehicle id so the form (and its useForm instance) mounts
        fresh for each vehicle, initialised directly from loaded data. This is
        what keeps the controlled Radix Selects in sync after a save.
      */}
      <VehicleForm
        key={vehicle?.id ?? 'new'}
        vehicle={vehicle ?? null}
        accountId={activeAccount.id}
        basePath={basePath}
        laVehicleTypes={laVehicleTypes ?? []}
        quoteNotifySettings={quoteSettings?.notify ?? null}
        customQuoteSettings={quoteSettings?.customQuotes ?? null}
      />
    </div>
  )
}
